import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, BACKEND } from "@/lib/api";
import { RichEditor, uploadImage } from "@/components/admin/RichEditor";

const EMPTY = { title: "", slug: "", excerpt: "", content_html: "", cover_image: "", status: "draft", meta_title: "", meta_description: "" };
const field = "mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 text-slate-900 outline-none transition-colors focus:border-violet-500";

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[13px] font-medium text-slate-700">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-[12px] text-slate-500">{hint}</span>}
  </label>
);

const Cover = ({ value, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div>
      <span className="text-[13px] font-medium text-slate-700">Cover image</span>
      <div className="relative mt-2 aspect-[16/9] overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-[#F3F2F8]">
        {value ? (
          <>
            <img src={fileUrl(value)} alt="Cover preview" className="h-full w-full object-cover" data-testid="cover-preview" />
            <button type="button" onClick={() => onChange("")} data-testid="cover-remove" className="absolute right-3 top-3 rounded-full bg-black/60 p-2 text-white hover:bg-black/80" aria-label="Remove cover"><X className="h-4 w-4" /></button>
          </>
        ) : (
          <button type="button" onClick={() => ref.current?.click()} data-testid="cover-upload-button" className="flex h-full w-full flex-col items-center justify-center gap-2 text-sm text-slate-600 hover:text-violet-700">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />} {busy ? "Uploading…" : "Upload from your computer"}
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pick} data-testid="cover-input" />
    </div>
  );
};

export default function AdminEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [post, setPost] = useState(isNew ? EMPTY : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return undefined;
    let alive = true;
    api.get(`/admin/posts/${id}`).then((r) => alive && setPost({ ...EMPTY, ...r.data })).catch((e) => { toast.error(errMsg(e)); nav("/admin"); });
    return () => { alive = false; };
  }, [id, isNew, nav]);

  if (!post) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-violet-700" /></div>;
  const set = (k) => (e) => setPost((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async (status) => {
    if (!post.title.trim()) { toast.error("Please add a title"); return; }
    setSaving(true);
    const body = { ...post, status, content_html: post.content_html.split(`${BACKEND}/api/`).join("/api/") };
    try {
      const { data } = isNew ? await api.post("/admin/posts", body) : await api.put(`/admin/posts/${id}`, body);
      toast.success(status === "published" ? "Post published" : "Draft saved");
      setPost((p) => ({ ...p, slug: data.slug, status: data.status }));
      if (isNew) nav(`/admin/posts/${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-editor-page">
      <Link to="/admin" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-violet-700"><ArrowLeft className="h-4 w-4" /> All posts</Link>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl font-medium text-slate-900">{isNew ? "New post" : "Edit post"}</h1>
        <div className="flex gap-2">
          <button type="button" disabled={saving} onClick={() => save("draft")} data-testid="admin-save-draft" className="h-11 rounded-full border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-800 hover:border-violet-600 disabled:opacity-60">{post.status === "published" ? "Unpublish" : "Save draft"}</button>
          <button type="button" disabled={saving} onClick={() => save("published")} data-testid="admin-publish" className="inline-flex h-11 items-center gap-2 rounded-full bg-[#0B0716] px-5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />}{post.status === "published" ? "Update" : "Publish"}</button>
        </div>
      </div>
      <div className="mt-8 grid gap-8 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          <input value={post.title} onChange={set("title")} placeholder="Post title" data-testid="post-title-input"
            className="w-full rounded-2xl border border-slate-200 bg-white px-6 py-5 font-display text-3xl text-slate-900 outline-none focus:border-violet-500" />
          <RichEditor key={isNew ? "new" : id} initial={post.content_html} onChange={set("content_html")} />
        </div>
        <aside className="space-y-6 lg:col-span-4">
          <div className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6">
            <Cover value={post.cover_image} onChange={set("cover_image")} />
            <Field label="Excerpt" hint="Short summary shown on the Insights page.">
              <textarea rows={4} value={post.excerpt} onChange={set("excerpt")} maxLength={500} data-testid="post-excerpt-input" className={`${field} py-3`} />
            </Field>
            <Field label="URL slug" hint={`perfectdigitals…/insights/${post.slug || "auto-from-title"}`}>
              <input value={post.slug} onChange={set("slug")} placeholder="auto-from-title" data-testid="post-slug-input" className={`${field} h-11`} />
            </Field>
          </div>
          <div className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-violet-700">Search (SEO)</p>
            <Field label="Meta title" hint="Defaults to the post title.">
              <input value={post.meta_title} onChange={set("meta_title")} maxLength={200} data-testid="post-meta-title-input" className={`${field} h-11`} />
            </Field>
            <Field label="Meta description" hint="Defaults to the excerpt.">
              <textarea rows={3} value={post.meta_description} onChange={set("meta_description")} maxLength={320} data-testid="post-meta-desc-input" className={`${field} py-3`} />
            </Field>
          </div>
        </aside>
      </div>
    </div>
  );
}
